import { useCallback, useMemo, useState, useEffect } from 'react';
import { IEditorProps } from 'components/Editors/Editor';
import { EditorPopup } from 'components/Editors/EditorPopup';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderNode';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { DesignerColor } from '@decisions/server/Decisions/Silverlight/UI/Utilities/DesignerColor';
import { getColorCssString, getColorEditorString, getDefaultDesignerColor, rgbToHex } from 'utils/ColorUtils';
import { ColorSwatch } from 'components/inputs/color-pickers/ColorSwatch';

interface IColorEditorProps extends IEditorProps {}

interface IColorChangeHandler {
  (designerColor: DesignerColor): void;
}

export interface IColorPickerDialogOptions {
  stickTo?: Element; //JQuery;
  title?: string;
  width?: number;
  height?: number;
  onOk?: (designerColor: DesignerColor) => void;
  onClosed?: () => void;
  onClear?: () => void;
  showGradients?: boolean; // custom / client side only
}

const ColorEditorClassName = 'dpc-editor-color';
const getClassName = () => ColorEditorClassName;

/**
 * Editor Color "Input" for Property Grid. Does not include color picker dialogue at this time.
 *
 *
 * Enhancement Ideas:
 * https://casesandberg.github.io/react-color/ - pretty color dialog
 * https://www.npmjs.com/package/react-circle-slider - circle slider for gradient angle
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function ColorEditor(props: IColorEditorProps) {
  const { child, path, onChange } = props;
  const [colorString, setColorString] = useState(getColorEditorString(child));

  useEffect(() => setColorString(getColorEditorString(child)), [child]);

  const onOk = useCallback(
    (designerColor: DesignerColor) => {
      setColorString(getColorCssString(designerColor));
      onChange(getNewValue(designerColor, child), path);
    },
    [child, onChange, path]
  );

  const onClear = useCallback(() => {
    setColorString(getColorCssString(getDefaultDesignerColor()));
    onChange(null, path);
  }, [onChange, path]);

  const options = useMemo(() => getDialogOptions(onClear, onOk, child), [child, onClear, onOk]);

  return (
    <EditorPopup {...props} dialogOptions={options} className={getClassName()}>
      <ColorSwatch color={colorString} />
    </EditorPopup>
  );
}

function canShowGradients(child: ContextProviderNode): boolean {
  return !getEditorConfigValue(child, 'PickSolidColorOnly');
}

function getNewValue(value: DesignerColor, child: ContextProviderNode) {
  return usesHexCode(child) ? rgbToHex(value.ColorName) : value;
}

function usesHexCode(child: ContextProviderNode): boolean {
  return !!getEditorConfigValue(child, 'GetSetHexCode');
}

function getDialogOptions(
  onClear: () => void,
  onOk: IColorChangeHandler,
  child: ContextProviderNode
): IColorPickerDialogOptions {
  return {
    onClear,
    onOk,
    showGradients: canShowGradients(child),
  };
}
